import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft, CirclePlus, Save, Trash2 } from 'lucide-react';
import {
  getAppUser,
  createAppUser,
  updateAppUser,
  deleteAppUser,
  getAppUserVehicles,
  replaceAppUserVehicles,
} from '../../services/api';
import { getErrorMessage } from '../../utils/errors';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import type { AppUser, AppUserVehicle } from '../../types';
import { PageHeader, Alert, Button, Card, CardContent, CardHeader, CardTitle, Field, Input, Separator, Spinner, Textarea } from '@racing/ui';

/**
 * Field tables rather than repeated markup — the three contact/driver/
 * membership sections are the same shape, and writing them out longhand is how
 * the MUI version ended up with inconsistent grid spans between them.
 */
type FieldSpec = {
  key: keyof AppUser;
  label: string;
  type?: 'text' | 'email' | 'tel' | 'date';
  wide?: boolean;
};

const CONTACT_FIELDS: FieldSpec[] = [
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'phone_number', label: 'Phone Number', type: 'tel' },
  { key: 'address', label: 'Address', wide: true },
  { key: 'city', label: 'City' },
  { key: 'state_province', label: 'State/Province' },
  { key: 'postal_code', label: 'Postal Code' },
  { key: 'country', label: 'Country' },
];

const DRIVER_FIELDS: FieldSpec[] = [
  { key: 'occupation', label: 'Occupation' },
  { key: 'license_number', label: 'License Number' },
  { key: 'license_expiry', label: 'License Expiry', type: 'date' },
  { key: 'home_division', label: 'Home Division' },
];

const MEMBERSHIP_FIELDS: FieldSpec[] = [
  { key: 'member_number', label: 'Member Number' },
  { key: 'member_expiry', label: 'Member Expiry', type: 'date' },
  { key: 'owner', label: 'Owner' },
  { key: 'crew_chief', label: 'Crew Chief' },
];

const VEHICLE_FIELDS: Array<{ key: keyof AppUserVehicle; label: string }> = [
  { key: 'nickname', label: 'Nickname' },
  { key: 'car_number', label: 'Car Number' },
  { key: 'category', label: 'Category' },
  { key: 'engine_make', label: 'Engine Make' },
  { key: 'engine_year', label: 'Engine Year' },
  { key: 'body_type', label: 'Body Type' },
  { key: 'body_year', label: 'Body Year' },
  { key: 'cui_cc', label: 'CUI / CC' },
  { key: 'hp', label: 'HP' },
  { key: 'factored_hp', label: 'Factored HP' },
];

const emptyVehicle: Partial<AppUserVehicle> = Object.fromEntries(
  VEHICLE_FIELDS.map(({ key }) => [key, null])
) as Partial<AppUserVehicle>;

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="flex flex-col gap-4">
    <h2 className="font-sans text-xs font-semibold uppercase tracking-[0.12em] text-ink-faint">
      {title}
    </h2>
    {children}
  </section>
);

const AppUserDetailPage = () => {
  const { appUserId } = useParams<{ appUserId: string }>();
  const navigate = useNavigate();

  /**
    * The `/admin/app-users/new` route declares no `:appUserId`, so the param is
    * undefined there — NOT the string "new". Comparing against "new" makes
    * `isNew` permanently false and sends the create flow straight back to the
    * list via the invalid-id guard below.
    */
  const isNew = !appUserId;
  const id = appUserId ? Number(appUserId) : 0;
  const isInvalidId = !isNew && !Number.isInteger(id);

  const [appUser, setAppUser] = useState<Partial<AppUser>>({});
  const [vehicles, setVehicles] = useState<Partial<AppUserVehicle>[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadAppUser = useCallback(async () => {
    if (isNew || isInvalidId) return;
    try {
      setLoading(true);
      const [userData, vehicleData] = await Promise.all([
        getAppUser(id),
        getAppUserVehicles(id),
      ]);
      setAppUser(userData);
      setVehicles(vehicleData);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [isNew, isInvalidId, id]);

  useEffect(() => {
    loadAppUser();
  }, [loadAppUser]);

  const handleChange = (field: keyof AppUser, value: string) =>
    setAppUser((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      if (isNew) {
        const created = await createAppUser(appUser);
        if (vehicles.length > 0) await replaceAppUserVehicles(created.id, vehicles);
        setSuccess('App user created');
        navigate(`/admin/app-users/${created.id}`, { replace: true });
      } else {
        await updateAppUser(id, appUser);
        await replaceAppUserVehicles(id, vehicles);
        setVehicles(await getAppUserVehicles(id));
        setSuccess('App user saved');
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    try {
      await deleteAppUser(id);
      navigate('/admin/app-users');
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  };

  const renderField = ({ key, label, type = 'text', wide }: FieldSpec) => (
    <Field key={String(key)} label={label} className={wide ? 'sm:col-span-2' : undefined}>
      {(p) => (
        <Input
          type={type}
          value={(appUser[key] as string) || ''}
          onChange={(e) => handleChange(key, e.target.value)}
          {...p}
        />
      )}
    </Field>
  );

  if (isInvalidId) return <Navigate to="/admin/app-users" replace />;

  if (loading) {
    return (
      <div className="flex justify-center pt-20">
        <Spinner className="size-8" />
      </div>
    );
  }

  const title = isNew
    ? 'New App User'
    : appUser.name || [appUser.first_name, appUser.last_name].filter(Boolean).join(' ') || 'App User';

  return (
    <div className="flex min-w-0 flex-col">
      <Button
        variant="ghost"
        size="sm"
        className="mb-2 -ml-2 self-start"
        onClick={() => navigate('/admin/app-users')}
      >
        <ArrowLeft /> Back to app users
      </Button>

      <PageHeader
        title={title}
        subtitle="Driver account from the registration app. These accounts do not sign in to Control Center."
        actions={
          <>
            {!isNew && (
              <Button variant="destructive-outline" onClick={() => setConfirmOpen(true)}>
                <Trash2 /> Delete
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Spinner className="size-4 text-white" label={null} /> : <Save />}
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      />

      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" className="mb-4" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <div className="flex flex-col gap-6">
        <Section title="Contact information">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {CONTACT_FIELDS.map(renderField)}
          </div>
        </Section>

        <Separator />

        <Section title="Driver info">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {DRIVER_FIELDS.map(renderField)}
            <Field label="Driver Bio" className="sm:col-span-2 lg:col-span-4">
              {(p) => (
                <Textarea
                  rows={3}
                  value={appUser.driver_bio || ''}
                  onChange={(e) => handleChange('driver_bio', e.target.value)}
                  {...p}
                />
              )}
            </Field>
          </div>
        </Section>

        <Separator />

        <Section title="Membership">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {MEMBERSHIP_FIELDS.map(renderField)}
          </div>
        </Section>

        <Separator />

        <Section title="Vehicles">
          <div className="flex flex-col gap-3">
            {vehicles.length === 0 && (
              <p className="rounded-md border border-dashed border-line-strong px-3 py-4 text-center font-sans text-sm text-ink-faint">
                No vehicles recorded for this driver.
              </p>
            )}

            {vehicles.map((vehicle, index) => (
              // Index keys are correct here: rows have no id until saved, and
              // add/remove always rebuilds the array.
              <Card key={index}>
                <CardHeader className="flex-row items-center justify-between gap-2 pb-3">
                  <CardTitle className="text-base">
                    {vehicle.nickname || vehicle.car_number || `Vehicle ${index + 1}`}
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove vehicle ${index + 1}`}
                    className="text-danger hover:bg-danger-bg"
                    onClick={() => setVehicles((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <Trash2 />
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    {VEHICLE_FIELDS.map(({ key, label }) => (
                      <Field key={String(key)} label={label}>
                        {(p) => (
                          <Input
                            value={(vehicle[key] as string) || ''}
                            onChange={(e) =>
                              setVehicles((prev) =>
                                prev.map((v, i) =>
                                  i === index ? { ...v, [key]: e.target.value } : v
                                )
                              )
                            }
                            {...p}
                          />
                        )}
                      </Field>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}

            <div>
              <Button
                variant="outline"
                onClick={() => setVehicles((prev) => [...prev, { ...emptyVehicle }])}
              >
                <CirclePlus /> Add Vehicle
              </Button>
            </div>
          </div>
        </Section>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete app user?"
        message="This removes the driver account and its vehicles. Registrations already made are not deleted."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default AppUserDetailPage;
